import { BiBlendJoin, BiBlendKey, kBiBlendSides } from '@euler/model/bi/blend';
import {
  BiCatalogField,
  BiConformedKey,
  BiDatasetCatalog,
} from '@euler/model/bi/catalog';
import {
  BiDatasetRef,
  BiDimensionRef,
  BiTimeGrain,
} from '@euler/model/bi/query';
import { BiFieldRole } from '@euler/model/bi/result';
import {
  BiEditorBlend,
  BiEditorQuery,
  BiEditorState,
  biEditorQueryNew,
} from './biEditorState';

/** Mirror of the server's cap on a query's dimensions (and `on` keys). */
export const kBiMaxBlendKeys = 3;

/** The two sides' catalogs, first query first. */
export type BiBlendCatalogs = [BiDatasetCatalog, BiDatasetCatalog];

/** A conformed key both datasets carry, with the fields carrying it. */
export interface BiBlendKeyOption {
  key: BiConformedKey;
  /** Per side, its dimension fields carrying the key. */
  fields: [BiCatalogField[], BiCatalogField[]];
}

const kGrainOrder = [
  BiTimeGrain.Month,
  BiTimeGrain.Week,
  BiTimeGrain.Day,
  BiTimeGrain.Quarter,
  BiTimeGrain.Year,
];

function keyFields(catalog: BiDatasetCatalog, key: BiConformedKey) {
  return catalog.fields.filter(
    field => field.role === BiFieldRole.Dimension && field.conformedKey === key,
  );
}

/** The keys offered: those a dimension field of each catalog carries. */
export function biBlendKeyOptions(
  catalogs: BiBlendCatalogs,
): BiBlendKeyOption[] {
  return Object.values(BiConformedKey).flatMap(key => {
    const first = keyFields(catalogs[0], key);
    const second = keyFields(catalogs[1], key);
    return first.length && second.length
      ? [
          {
            key,
            fields: [first, second] as [BiCatalogField[], BiCatalogField[]],
          },
        ]
      : [];
  });
}

/** The grains a `date` key may take: those both picked fields allow. */
export function biBlendGrains(
  fields: [BiCatalogField | undefined, BiCatalogField | undefined],
): BiTimeGrain[] {
  const [a, b] = fields;
  if (!a?.grains || !b?.grains) return [];
  return a.grains.filter(grain => b.grains!.includes(grain));
}

/** The side's dimension standing for the key, by the field carrying it. */
export function biBlendSideDimension(
  catalog: BiDatasetCatalog,
  query: BiEditorQuery,
  key: BiConformedKey,
): BiDimensionRef | undefined {
  return query.dimensions.find(
    ref =>
      catalog.fields.find(field => field.key === ref.field)?.conformedKey ===
      key,
  );
}

/** A side's candidates, its primary time first: the natural date. */
function byPreference(
  catalog: BiDatasetCatalog,
  candidates: BiCatalogField[],
): BiCatalogField[] {
  return [
    ...candidates.filter(field => field.key === catalog.primaryTime),
    ...candidates.filter(field => field.key !== catalog.primaryTime),
  ];
}

const dimensionOf = (field: string, grain: BiTimeGrain | undefined) =>
  grain ? { field, grain } : { field };

function withSides(
  state: BiEditorState,
  blend: BiEditorBlend,
  map: (query: BiEditorQuery, side: number) => BiDimensionRef[],
): BiEditorState {
  return {
    ...state,
    dimensions: map(state, 0),
    blend: {
      ...blend,
      second: { ...blend.second, dimensions: map(blend.second, 1) },
    },
  };
}

/** Combine with a second dataset; the merge keys are picked next. */
export function biEditorStartBlend(state: BiEditorState): BiEditorState {
  return {
    ...state,
    // Each side groups by exactly the merge keys, none picked yet.
    dimensions: [],
    sort: [],
    blend: { second: biEditorQueryNew(), on: [], join: BiBlendJoin.Left },
  };
}

/** Back to one dataset: the first query keeps its merge-key dimensions. */
export function biEditorStopBlend(state: BiEditorState): BiEditorState {
  return { ...state, blend: null };
}

export function biEditorWithSecondDataset(
  state: BiEditorState,
  blend: BiEditorBlend,
  dataset: BiDatasetRef,
): BiEditorState {
  return {
    ...state,
    dimensions: [],
    blend: {
      ...blend,
      on: [],
      second: { ...biEditorQueryNew(), dataset },
    },
  };
}

/**
 * Adds a merge key: each side groups by its field carrying it (the primary
 * time for `date`), a `date` at a grain both fields allow, month first.
 * Null when the key cannot be added (no common grain, or too many keys).
 */
export function biEditorWithBlendKey(
  state: BiEditorState,
  blend: BiEditorBlend,
  catalogs: BiBlendCatalogs,
  option: BiBlendKeyOption,
): BiEditorState | null {
  if (blend.on.length >= kBiMaxBlendKeys) return null;
  if (blend.on.some(entry => entry.key === option.key)) return null;
  const firsts = byPreference(catalogs[0], option.fields[0]);
  const seconds = byPreference(catalogs[1], option.fields[1]);
  let fields = [firsts[0], seconds[0]];
  let grain: BiTimeGrain | undefined = undefined;
  if (option.key === BiConformedKey.Date) {
    // The first pair of date fields, primary times first, sharing a grain.
    const pair = firsts
      .flatMap(a => seconds.map(b => [a, b]))
      .find(([a, b]) => biBlendGrains([a, b]).length > 0);
    if (!pair) return null;
    fields = pair;
    const grains = biBlendGrains([pair[0], pair[1]]);
    grain = kGrainOrder.find(entry => grains.includes(entry));
    if (!grain) return null;
  }
  const on: BiBlendKey = grain
    ? { key: option.key, grain }
    : { key: option.key };
  return withSides(
    state,
    { ...blend, on: [...blend.on, on] },
    (query, side) => [
      ...query.dimensions,
      dimensionOf(fields[side].key, grain),
    ],
  );
}

export function biEditorWithoutBlendKey(
  state: BiEditorState,
  blend: BiEditorBlend,
  catalogs: BiBlendCatalogs,
  key: BiConformedKey,
): BiEditorState {
  return withSides(
    state,
    { ...blend, on: blend.on.filter(entry => entry.key !== key) },
    (query, side) => {
      const standing = biBlendSideDimension(catalogs[side], query, key);
      return query.dimensions.filter(ref => ref !== standing);
    },
  );
}

/** A `date` key's grain, on the key and on both sides' dimensions. */
export function biEditorWithBlendGrain(
  state: BiEditorState,
  blend: BiEditorBlend,
  catalogs: BiBlendCatalogs,
  grain: BiTimeGrain,
): BiEditorState {
  return withSides(
    state,
    {
      ...blend,
      on: blend.on.map(entry =>
        entry.key === BiConformedKey.Date ? { ...entry, grain } : entry,
      ),
    },
    (query, side) => {
      const standing = biBlendSideDimension(
        catalogs[side],
        query,
        BiConformedKey.Date,
      );
      return query.dimensions.map(ref =>
        ref === standing ? { field: ref.field, grain } : ref,
      );
    },
  );
}

/**
 * Which of a side's fields stands for a key, when several carry it. Null
 * for a field that does not allow the `date` key's grain.
 */
export function biEditorWithBlendField(
  state: BiEditorState,
  blend: BiEditorBlend,
  catalogs: BiBlendCatalogs,
  side: number,
  key: BiConformedKey,
  field: BiCatalogField,
): BiEditorState | null {
  if (side < 0 || side >= kBiBlendSides) return null;
  const on = blend.on.find(entry => entry.key === key);
  if (!on) return null;
  if (on.grain && !field.grains?.includes(on.grain)) return null;
  return withSides(state, blend, (query, at) => {
    if (at !== side) return query.dimensions;
    const standing = biBlendSideDimension(catalogs[at], query, key);
    return query.dimensions.map(ref =>
      ref === standing ? dimensionOf(field.key, on.grain) : ref,
    );
  });
}
